import { motion } from "framer-motion";


const P = ({ children }) => <p className="font-body text-[15px] leading-[1.85] text-[#D9D9D2] mb-4 text-justify">{children}</p>;
const H = ({ children }) => <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2 mb-6 pb-3 border-b border-[#D8B23E]/30">{children}</h2>;
const Cap = ({ children }) => <div className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#8A8A82] mt-2">{children}</div>;
const API = process.env.REACT_APP_BACKEND_URL;
const Sig = ({ name, title, ink }) => (
  <div className="mt-8 inline-block min-w-[280px] border-t border-[#D8B23E]/60 pt-3" data-testid={ink ? "fm-signature-ink" : "fm-signature-plate"}>
    {ink ? (
      <img src={`${API}/api/manual/signature/${ink}.png`} alt={`${name} signature`} className="h-14 w-auto bg-white/95 px-3 py-1 rounded-sm" />
    ) : (
      <div className="font-serif italic text-2xl text-[#5B8DEF] leading-none py-2">{name}</div>
    )}
    <div className="font-serif text-lg text-[#F5F5F0] mt-2">{name}</div>
    <div className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-[#D8B23E]">{title}</div>
    <div className="text-[9px] font-grotesk uppercase tracking-[0.18em] text-[#8A8A82] mt-1">{ink ? "Blue ink · standard / board class per Vinci signature policy" : "Specimen signature lodged in the Signatory Vault · Clause 3.5"}</div>
  </div>
);
const KV = ({ rows }) => (
  <div className="border border-[#D8B23E]/30 rounded-sm overflow-hidden">
    <table className="w-full text-[13px]"><tbody>
      {rows.map((r) => (
        <tr key={r.field} className="border-t first:border-t-0 border-white/10">
          <td className="px-4 py-2.5 bg-[#12100A] font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E] w-[34%] align-top">{r.field}</td>
          <td className="px-4 py-2.5 text-[#D9D9D2] font-body">{r.value}</td>
        </tr>))}
    </tbody></table>
  </div>
);

function Portrait({ src, caption }) {
  return (
    <figure className="w-full md:w-[260px] shrink-0" data-testid="fm-photo">
      <img src={src} alt={caption} className="w-full aspect-[4/5] object-cover border border-[#D8B23E]/40" />
      <figcaption className="text-[11px] italic text-[#C9C9C2] mt-2 font-body">{caption}</figcaption>
    </figure>
  );
}

export function FrontMatter({ id, fm, parts, onPick }) {
  const L = fm.leadership;
  switch (id) {
    case "fm-cover":
      return (
        <div className="border border-[#D8B23E]/50 p-10 md:p-16 text-center bg-[#070706] relative overflow-hidden" data-testid="fm-cover">
          <div className="absolute inset-3 border border-[#D8B23E]/20 pointer-events-none" />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#D8B23E]">Vinci Incorporation</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F0] mt-8">{fm.cover.title}</h1>
          <div className="font-grotesk text-[11px] uppercase tracking-[0.34em] text-[#D8B23E] mt-3">{fm.cover.subtitle}</div>
          <p className="font-serif italic text-xl text-[#C9C9C2] mt-12">“{fm.cover.tagline}”</p>
          <div className="mt-16 text-[13px] text-[#C9C9C2] font-body">{fm.cover.entity}</div>
          <div className="mt-2 font-mono text-[12px] text-[#8A8A82]">{fm.cover.version_line}</div>
          <div className="mt-10 inline-block border border-[#E5484D]/60 text-[#E5484D] px-4 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.3em]">{fm.cover.classification}</div>
        </div>);
    case "fm-version":
      return <div data-testid="fm-version"><H>Version Control</H><KV rows={fm.version_control} /><Cap>Updated upon every amendment approved under Clause 1.5</Cap></div>;
    case "fm-foreword":
      return (<div data-testid="fm-foreword"><H>Foreword from the Vinci Trade Desk</H>
        <div className="flex flex-col md:flex-row gap-8">
          <Portrait src={fm.images.executive_director} caption={L.executive_director.caption} />
          <div>{fm.trade_desk_foreword.map((p, i) => <P key={i}>{p}</P>)}<Sig ink="standard" name={L.executive_director.name} title={`${L.executive_director.title}, Mumbai`} /></div>
        </div></div>);
    case "fm-message":
      return (<div data-testid="fm-message"><H>Message from the Vinci Trade Desk</H>
        <div className="flex flex-col md:flex-row gap-8">
          <Portrait src={fm.images.global_head} caption={L.global_head.caption} />
          <div>{fm.global_head_message.map((p, i) => <P key={i}>{p}</P>)}<Sig name={L.global_head.name} title={`${L.global_head.title}, Mumbai`} /></div>
        </div></div>);
    case "fm-about":
      return <div data-testid="fm-about"><H>About Vinci Inc.</H>{fm.about.map((p, i) => <P key={i}>{p}</P>)}</div>;
    case "fm-philosophy":
      return (<div data-testid="fm-philosophy"><H>Vinci Core Trade Philosophy</H>
        <P>The operating conduct of Vinci Incorporation is governed by five doctrinal principles. Each clause of this Manual gives effect to one or more of them.</P>
        <div className="grid md:grid-cols-1 gap-3 mt-6">
          {fm.philosophy.map((p, i) => (
            <motion.div key={p.principle} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                        className="border border-[#D8B23E]/30 bg-[#0A0906] p-5 flex gap-5">
              <div className="font-serif text-3xl text-[#D8B23E] w-10 shrink-0">{i + 1}</div>
              <div><div className="font-serif text-lg text-[#F5F5F0]">{p.principle}</div><p className="text-[14px] text-[#D9D9D2] leading-relaxed mt-1 font-body">{p.doctrine}</p></div>
            </motion.div>))}
        </div></div>);
    case "fm-abbreviations":
      return (<div data-testid="fm-abbreviations"><H>List of Abbreviations &amp; Defined Terms</H>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#D8B23E] mb-2">Table FM-1 — Abbreviations</div>
        <div className="border border-[#D8B23E]/30 rounded-sm overflow-hidden mb-8"><table className="w-full text-[13px]"><tbody>
          {fm.abbreviations.map((a) => <tr key={a.term} className="border-t first:border-t-0 border-white/10"><td className="px-4 py-2 font-mono text-[#D8B23E] w-[30%]">{a.term}</td><td className="px-4 py-2 text-[#D9D9D2]">{a.meaning}</td></tr>)}
        </tbody></table></div>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#D8B23E] mb-2">Table FM-2 — Defined Terms</div>
        <div className="border border-[#D8B23E]/30 rounded-sm overflow-hidden"><table className="w-full text-[13px]"><tbody>
          {fm.defined_terms.map((a) => <tr key={a.term} className="border-t first:border-t-0 border-white/10"><td className="px-4 py-2 font-serif text-[#F5F5F0] w-[30%] align-top">{a.term}</td><td className="px-4 py-2 text-[#D9D9D2]">{a.meaning}</td></tr>)}
        </tbody></table></div></div>);
    case "fm-contents":
      return (<div data-testid="fm-contents"><H>Table of Contents</H>
        {parts.map((p) => (
          <div key={p.num} className="mb-6">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#D8B23E] border-b border-[#D8B23E]/30 pb-1 mb-2">Part {p.num} — {p.title}</div>
            {p.chapters.map((ch) => (
              <div key={ch.num} className="mb-2 pl-3">
                <div className="font-serif text-[14px] text-[#F5F5F0]">Chapter {ch.num} — {ch.title}</div>
                {ch.sections.map((s) => (
                  <button key={s.id} onClick={() => onPick(s.id)} className="block w-full text-left pl-5 py-0.5 text-[12.5px] text-[#C9C9C2] hover:text-[#D8B23E]" data-testid={`fm-toc-${s.id}`}>
                    <span className="font-mono text-[#D8B23E] mr-2">{s.id}</span>{s.title}
                  </button>))}
              </div>))}
          </div>))}
      </div>);
    default:
      return null;
  }
}
